import { SOLANA_CHAIN_ID, SOLANA_CHAIN_REFERENCE } from "@repo/caip";

/**
 * Networks whose Glider indexers can account for deposits and swaps.
 *
 * Keep provider-specific identifiers here so wallet discovery, stream intake,
 * and accounting use one chain registry. `codexNetworkId` is Codex's numeric
 * network identifier; EVM values are their EIP-155 chain ids.
 */
export const ACTIVE_INDEXED_NETWORKS = [
  {
    canonicalNetwork: "base-mainnet",
    chainNamespace: "eip155",
    chainReference: "8453",
    codexNetworkId: 8453,
    quickNodeStreamEnabled: true,
    quickNodeSlugVariants: ["base-mainnet"],
    vm: "evm",
  },
  {
    canonicalNetwork: "ethereum-mainnet",
    chainNamespace: "eip155",
    chainReference: "1",
    codexNetworkId: 1,
    quickNodeStreamEnabled: true,
    quickNodeSlugVariants: ["ethereum-mainnet"],
    vm: "evm",
  },
  {
    canonicalNetwork: "arbitrum-mainnet",
    chainNamespace: "eip155",
    chainReference: "42161",
    codexNetworkId: 42161,
    quickNodeStreamEnabled: true,
    quickNodeSlugVariants: ["arbitrum-mainnet"],
    vm: "evm",
  },
  {
    canonicalNetwork: "bsc-mainnet",
    chainNamespace: "eip155",
    chainReference: "56",
    codexNetworkId: 56,
    quickNodeStreamEnabled: true,
    quickNodeSlugVariants: [
      "bnb-smartchain-mainnet",
      "bnbchain-mainnet",
      "bsc-mainnet",
    ],
    vm: "evm",
  },
  {
    canonicalNetwork: "robinhood-mainnet",
    chainNamespace: "eip155",
    chainReference: "4663",
    codexNetworkId: 4663,
    quickNodeStreamEnabled: true,
    quickNodeSlugVariants: ["robinhood-chain-mainnet", "robinhood-mainnet"],
    vm: "evm",
  },
  {
    canonicalNetwork: "monad-mainnet",
    chainNamespace: "eip155",
    chainReference: "143",
    codexNetworkId: 143,
    quickNodeStreamEnabled: true,
    quickNodeSlugVariants: ["monad-mainnet"],
    vm: "evm",
  },
  {
    canonicalNetwork: "solana-mainnet",
    chainNamespace: "solana",
    chainReference: SOLANA_CHAIN_REFERENCE,
    codexNetworkId: SOLANA_CHAIN_ID,
    quickNodeStreamEnabled: false,
    quickNodeSlugVariants: [],
    vm: "svm",
  },
] as const;

export type ActiveIndexedNetwork = (typeof ACTIVE_INDEXED_NETWORKS)[number];

export type ActiveQuickNodeStreamNetwork = Extract<
  ActiveIndexedNetwork,
  { readonly quickNodeStreamEnabled: true }
>;

export const ACTIVE_QUICKNODE_STREAM_NETWORKS = ACTIVE_INDEXED_NETWORKS.filter(
  (network): network is ActiveQuickNodeStreamNetwork =>
    network.quickNodeStreamEnabled,
);

export const ACTIVE_INDEXED_CODEX_NETWORK_IDS_BY_VM = {
  evm: ACTIVE_INDEXED_NETWORKS.filter((network) => network.vm === "evm").map(
    (network) => network.codexNetworkId,
  ),
  svm: ACTIVE_INDEXED_NETWORKS.filter((network) => network.vm === "svm").map(
    (network) => network.codexNetworkId,
  ),
} as const satisfies Readonly<Record<"evm" | "svm", readonly number[]>>;
